<template>
  <v-card class="confirmation-dialog">
    <v-card-title data-testid="title">
      {{ componentText.title }}
    </v-card-title>
    <v-card-subtitle
      v-if="componentText.subtitle"
      class="pt-3"
      style="white-space: pre-line"
      data-testid="subtitle"
    >
      {{ componentText.subtitle }}
    </v-card-subtitle>

    <v-card-actions class="px-5">
      <div class="w-100 ga-1" :class="{ 'flex-row': !xs, 'flex-column': xs }">
        <v-spacer />
        <app-button
          variant="outlined"
          class="text-none"
          :loading="loading"
          :block="xs"
          data-testid="cancel-button"
          @click="dialog.closeDialog()"
        >
          Cancel
        </app-button>
        <app-button
          color="primary"
          class="text-none"
          :loading="loading"
          :block="xs"
          data-testid="confirm-button"
          @click="confirmAction"
        >
          I'm still here
        </app-button>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script setup>
import { parseErrorMessage } from "@/util/helpers";
import { useSnackbarStore } from "@/stores/snackbar";
import { useDialogStore } from "@/stores/dialog";
import { onBeforeUnmount, onMounted, ref, computed } from "vue";
import { useDisplay } from "vuetify";

const props = defineProps({
  title: { type: String, default: null },
  subtitle: { type: String, default: null },

  stillPresentFunc: { type: Function, default: () => {} },
  expiredFunc: { type: Function, default: () => {} },
  timeInSeconds: { type: Number, required: true }
});

const snackbar = useSnackbarStore();
const dialog = useDialogStore();
const { xs } = useDisplay();

const time = ref(props.timeInSeconds);

const componentText = computed(() => {
  if (time.value < 0) {
    return {
      title: "Your session has expired. You'll be redirected shortly.",
      subtitle: null
    };
  }

  return {
    title: `Your session is expiring in ${formatTime(time.value)}`,
    subtitle: "Please confirm that you're still present"
  };
});

let interval;
onMounted(() => {
  if (interval) clearInterval(interval);
  interval = setInterval(() => {
    time.value -= 1;
    if (time.value < -10) {
      props.expiredFunc();
    }
  }, 1000);
});

onBeforeUnmount(() => {
  if (interval) clearInterval(interval);
});

function formatTime(value) {
  if (value <= 0) return "0:00";
  const seconds = value % 60;
  const minutes = (value - seconds) / 60;

  let secondsText = seconds;
  if (secondsText < 10) secondsText = `0${seconds}`;

  return `${minutes}:${secondsText}`;
}

const loading = ref(false);
async function confirmAction() {
  loading.value = true;
  try {
    await props.stillPresentFunc();
    dialog.closeDialog();
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  } finally {
    loading.value = false;
  }
}
</script>
